import { AsyncPipe } from '@angular/common';
import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { WorkplaceDetailIcpsStore } from './workplace-detail-icps.store';
import { FilterInput } from '@it2go/data-grid';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FormBuilder, Validators } from '@angular/forms';
import { ServicePaymentGridItemObject, WorkplaceIcpGridItemObject } from '@it2go/types';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { isInvalid } from '../../../../shared/helpers/validate';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { FilterBuilder } from '../../../helper/filter.builder';
import { ActivatedRoute } from '@angular/router';
import { CardModule } from '@openng/optimus-ui/card';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { TranslatePipe } from '@ngx-translate/core';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import {
    InsuranceCompanySelectInputComponent,
} from '../../../../../components/medical-service/element/insurance-company-select-input/insurance-company-select-input.component';

@Component({
    selector: 'app-workplace-detail-icps',
    templateUrl: './workplace-detail-icps.component.html',
    providers: [WorkplaceDetailIcpsStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonsComponent,
        AsyncPipe,
        CardModule,
        DateInputComponent,
        DialogModule,
        PrimeTemplate,
        InsuranceCompanySelectInputComponent,
        TableComponent,
        TextInputComponent,
        TranslatePipe,
    ],
})
export class WorkplaceDetailIcpsComponent extends SubscriptionComponent implements OnInit {

    protected readonly store = inject(WorkplaceDetailIcpsStore);
    private readonly fb = inject(FormBuilder);
    private readonly confirmBuilder = inject(ConfirmBuilder);
    private readonly route = inject(ActivatedRoute);

    workplaceId: number | null = null;
    icpsAdditionalFilter: FilterInput[][] = [];

    form = this.fb.group({
        id: this.fb.control<number | null>(null),
        insuranceCompanyId: this.fb.control<number | null>(null, Validators.required),
        icp: this.fb.control<string | null>(null, [Validators.required, Validators.maxLength(8)]),
        validFrom: this.fb.control<Date>(new Date(), Validators.required),
    })

    modalVisible = false;
    insuranceCompaniesOptions: { name: string, value: number }[] = [];

    servicePaymentSettings: ServicePaymentGridItemObject[] = [];
    servicePaymentSettingsLoaded = false;

    columns: TableColumn[] = [
        {
            name: 'insuranceCompany',
            data: (item) => item.code + ' - ' + item.name,
            filterable: {
                type: 'select',
                selectOptions: () => this.insuranceCompaniesOptions,
            },
        },
        {
            name: 'icp',
            filterable: true,
        },
        {
            name: 'validFrom',
            type: TableColumnType.Date,
            filterable: undefined,
            sortable: true,
        },
        {
            name: 'validTo',
            type: TableColumnType.Date,
            filterable: undefined,
            sortable: true,
        },
    ];

    @ViewChild('icpsTable') icpsTable!: TableComponent;

    constructor() {
        super();

        this.store.doOnActions(
            () => this.icpsTable.reload(),
            'createWorkplaceIcp',
            'updateWorkplaceIcp',
            'deleteWorkplaceIcp',
        )

        this.store.registerSuccessMessage('createWorkplaceIcp', 'common.created');
        this.store.registerSuccessMessage('updateWorkplaceIcp', 'common.updated');
        this.store.registerSuccessMessage('deleteWorkplaceIcp', 'common.deleted');

        this.subs.push(
            this.store.routeParam.workplaceId.subscribe((workplaceId) => {
                if (workplaceId) {
                    this.workplaceId = Number(workplaceId);
                    this.icpsAdditionalFilter = [[{ column: 'workplaceId', operator: 'EQ', value: [workplaceId] }]];
                }
            }),
            this.store.insuranceCompanies$.subscribe((companies) => {
                this.insuranceCompaniesOptions = companies.map((company) => {
                    return {
                        name: company.code + ' - ' + company.name,
                        value: company.id,
                    };
                });
            }),
            this.store.servicePayments$.subscribe((servicePaymentSettings) => {
                this.servicePaymentSettings = servicePaymentSettings;
            }),
            this.form.controls.insuranceCompanyId.valueChanges.subscribe((insuranceCompanyId) => {
                if (insuranceCompanyId && !this.form.controls.icp.touched) {
                    this.form.patchValue({
                        icp: this.generateIcp(insuranceCompanyId),
                    });
                }
            }),
        )
    }

    ngOnInit() {
        this.store.api.getInsuranceCompanies(FilterBuilder.singlePage({}));
    }

    openCreateIcp() {
        if (!this.servicePaymentSettingsLoaded) {
            this.loadServicePaymentSettings();
            this.servicePaymentSettingsLoaded = true;
        }
        this.modalVisible = true;
    }

    openEditIcp(icp: WorkplaceIcpGridItemObject) {
        this.form.patchValue({
            id: icp.id,
            insuranceCompanyId: icp.insuranceCompany.id,
            icp: icp.icp,
            validFrom: icp.validFrom ? new Date(icp.validFrom) : new Date(),
        });
        this.modalVisible = true;
    }

    closeModal() {
        this.modalVisible = false;
        this.form.reset();
    }

    submit() {
        if (isInvalid(this.form)) return;

        const data = {
            validFrom: this.form.controls.validFrom.value!.toISOString(),
            insuranceCompanyId: this.form.controls.insuranceCompanyId.value!,
            icp: this.form.controls.icp.value!,
        }

        if (this.form.controls.id.value) {
            this.store.api.updateWorkplaceIcp({
                id: this.form.controls.id.value,
                ...data,
            });
        } else {
            this.store.api.createWorkplaceIcp({
                workplaceId: this.workplaceId!,
                ...data,
            });
        }
        this.closeModal();
    }

    delete(item: any) {
        this.confirmBuilder.confirm(
            'workplace.icps.deleteIcpConfirmation.header',
            'workplace.icps.deleteIcpConfirmation.label',
            () => {
                this.store.api.deleteWorkplaceIcp({ id: item.item.id });
            },
            { name: 'workplaceIcpDeleteConfirm' + item.item.id },
            'pi-trash',
            item.event.target,
        );
    }

    private loadServicePaymentSettings() {
        const serviceId = this.route.snapshot.params['serviceId'] ?? null;
        if (serviceId) {
            const filter = new FilterBuilder();

            filter.where('serviceId', serviceId, 'EQ');
            filter.where('validFrom', new Date().toISOString(), 'LTE');
            filter.where('validTo', new Date().toISOString(), 'GTE');

            this.store.api.getServicePayments(filter.filter());
        }
    }

    private generateIcp(insuranceCompanyId: number) {
        const servicePayment = this.servicePaymentSettings?.find((sp) => {
            return sp.insuranceCompanyId === insuranceCompanyId
        });

        if (servicePayment) {
            return servicePayment.serviceICZ.substring(0, 5) + '000';
        }
        return '';
    }
}
